import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { CheckCircle, XCircle, Eye } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';

export function ProcurementJobCard({ jo, isSpecial, onViewFiles, onApprove, onReject }) {
  return (
    <Card
      data-testid={`procurement-job-${jo.jo_number}`}
      className={`rounded-sm shadow-sm ${isSpecial ? 'border-2 border-orange-400 bg-orange-50/30' : 'border-zinc-300'}`}
    >
      <CardHeader className="border-b border-zinc-300">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-xl font-bold text-zinc-950">JO #{jo.jo_number}</CardTitle>
            <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Pending Approval</Badge>
            {jo.profile_type === 'Colour' && (
              <Badge data-testid={`colour-badge-${jo.jo_number}`} className="bg-orange-100 text-orange-800 border border-orange-300">Colour Profile</Badge>
            )}
            {jo.glass_type !== 'Normal' && (
              <Badge data-testid={`special-glass-badge-${jo.jo_number}`} className="bg-blue-100 text-blue-800 border border-blue-300">{jo.glass_type} Glass</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Customer</p>
            <p className="text-zinc-950 mt-1">{jo.customer_name}</p>
            <p className="text-sm text-zinc-500">{jo.customer_mobile}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Executive</p>
            <p data-testid={`procurement-executive-${jo.jo_number}`} className="text-zinc-950 mt-1">{jo.executive_name || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Location</p>
            <p className="text-zinc-950 mt-1">{jo.work_location}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Specifications</p>
            <p className="text-zinc-950 mt-1">{jo.no_of_windows} Windows</p>
            <p className="text-sm text-zinc-500">{jo.total_sqft} Sqft</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery Date</p>
            <p className="text-zinc-950 mt-1">{formatDate(jo.delivery_date) || jo.delivery_date}</p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button data-testid={`view-files-${jo.jo_number}`} onClick={() => onViewFiles(jo.uploaded_files)} variant="outline" className="h-11">
            <Eye size={20} weight="bold" className="mr-2" />
            View Files ({jo.uploaded_files?.length || 0})
          </Button>
          <Button data-testid={`approve-${jo.jo_number}`} onClick={() => onApprove(jo)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11">
            <CheckCircle size={20} weight="bold" className="mr-2" />
            Approve
          </Button>
          <Button data-testid={`reject-${jo.jo_number}`} onClick={() => onReject(jo)} variant="outline" className="h-11 border-red-300 text-red-700 hover:bg-red-50">
            <XCircle size={20} weight="bold" className="mr-2" />
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ApprovalDialog({ selectedJob, onClose, remarks, setRemarks, onConfirm, loading }) {
  if (!selectedJob) return null;
  const isReject = selectedJob.action === 'reject';
  return (
    <Dialog open={!!selectedJob} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isReject ? 'Reject' : 'Approve'} Job Order #{selectedJob.jo_number}
          </DialogTitle>
          <DialogDescription className="sr-only">Add remarks and confirm action</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Remarks (Optional)</label>
            <Textarea
              data-testid="approval-remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any remarks or notes"
              className="min-h-24"
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              data-testid="confirm-action-button"
              onClick={() => onConfirm(selectedJob.jo_number, isReject ? 'rejected' : 'approved')}
              disabled={loading}
              className={isReject ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            >
              {loading ? 'Processing...' : 'Confirm'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
